import { useEffect, useRef, useState } from "react";
import { Minus, Plus, Sun, Moon, X } from "lucide-react";
import { fmtTime } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { TypedText, useTypedLines } from "./typewriter";

const SIZES = ["text-4xl sm:text-5xl", "text-5xl sm:text-6xl", "text-5xl sm:text-7xl", "text-6xl sm:text-8xl"];

function useIdle(ms = 2500) {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    let t;
    const wake = () => { setIdle(false); clearTimeout(t); t = setTimeout(() => setIdle(true), ms); };
    wake();
    window.addEventListener("mousemove", wake);
    window.addEventListener("touchstart", wake);
    return () => { clearTimeout(t); window.removeEventListener("mousemove", wake); window.removeEventListener("touchstart", wake); };
  }, [ms]);
  return idle;
}

const CtrlBtn = ({ onClick, label, children, id, dark }) => (
  <button onClick={onClick} aria-label={label} title={label} data-testid={id}
    className={`grid h-11 w-11 place-items-center rounded-full transition-colors ${dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-900/5 text-slate-900 hover:bg-slate-900/10"}`}>{children}</button>
);

export const CaptionFullscreen = ({ live, state, elapsed, onClose }) => {
  const { L } = useApp();
  const ref = useRef(null);
  const [size, setSize] = useState(() => Number(localStorage.getItem("minitai-caption-size") ?? 2));
  const [dark, setDark] = useState(() => localStorage.getItem("minitai-caption-theme") !== "light");
  const idle = useIdle();
  const resize = (d) => setSize((s) => { const n = Math.max(0, Math.min(SIZES.length - 1, s + d)); localStorage.setItem("minitai-caption-size", n); return n; });
  const theme = () => setDark((v) => { localStorage.setItem("minitai-caption-theme", v ? "light" : "dark"); return !v; });

  useEffect(() => {
    ref.current?.requestFullscreen?.().catch(() => {});
    const onFs = () => { if (!document.fullscreenElement) onClose(); };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") resize(1);
      if (e.key === "-") resize(-1);
    };
    document.addEventListener("fullscreenchange", onFs);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      window.removeEventListener("keydown", onKey);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { typed, typing } = useTypedLines(live.lines, live.interim);
  const recent = typed.slice(-3);
  const empty = !recent.length;
  return (
    <div ref={ref} className={`fixed inset-0 z-[100] flex flex-col ${dark ? "bg-[#0b0b12] text-white" : "bg-white text-slate-900"} ${idle ? "cursor-none" : ""}`} data-testid="caption-fullscreen">
      <div className={`flex items-center gap-3 px-6 py-5 transition-opacity duration-500 sm:px-12 ${idle ? "opacity-0" : "opacity-100"}`}>
        <span className={`h-3 w-3 rounded-full ${state === "recording" ? "rec-pulse bg-red-500" : "bg-amber-400"}`} />
        <span className="font-mono text-lg" data-testid="caption-timer">{fmtTime(elapsed)}</span>
        <span className={`text-sm ${dark ? "text-white/50" : "text-slate-500"}`}>{state === "recording" ? L("Kapsyen langsung", "Live captions") : L("Dijeda", "Paused")}</span>
        <div className="ml-auto flex gap-2">
          <CtrlBtn dark={dark} onClick={() => resize(-1)} label={L("Kecilkan teks", "Smaller text")} id="caption-smaller-btn"><Minus size={18} /></CtrlBtn>
          <CtrlBtn dark={dark} onClick={() => resize(1)} label={L("Besarkan teks", "Larger text")} id="caption-larger-btn"><Plus size={18} /></CtrlBtn>
          <CtrlBtn dark={dark} onClick={theme} label={L("Tukar tema", "Toggle theme")} id="caption-theme-btn">{dark ? <Sun size={18} /> : <Moon size={18} />}</CtrlBtn>
          <CtrlBtn dark={dark} onClick={onClose} label={L("Keluar (Esc)", "Exit (Esc)")} id="caption-exit-btn"><X size={18} /></CtrlBtn>
        </div>
      </div>
      <div className={`flex flex-1 flex-col justify-end overflow-hidden px-6 pb-16 font-heading font-bold leading-[1.25] tracking-tight sm:px-16 ${SIZES[size]}`} data-testid="caption-text" aria-live="polite">
        {empty ? (
          <p className={`text-center text-3xl font-medium sm:text-4xl ${dark ? "text-white/40" : "text-slate-400"}`}>{L("Menunggu pertuturan…", "Waiting for speech…")}</p>
        ) : (
          <>
            {recent.map((it, i) => (
              <p key={it.key} className={`mb-4 transition-opacity duration-500 ${i < recent.length - 1 ? (dark ? "opacity-40" : "opacity-35") : ""}`} data-testid={it.final ? `caption-line-${it.id}` : "caption-interim"}>
                <TypedText text={it.typed} />{i === recent.length - 1 && (state === "recording" || typing) && <span className="live-caret" />}
              </p>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
